import {customElement, property} from 'lit/decorators.js';
import {CSSResult, html, LitElement, TemplateResult, unsafeCSS} from 'lit';
import {Messages, Product} from '../../../@types/wishlist.js';
import '../wishlist-image/wishlist-image.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {consume} from '@lit/context';
import plusIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_plus.svg';
import css from './wishlist-item-slider-item.scss';
import checkmarkIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_check.svg';
import {classMap} from 'lit/directives/class-map.js';
import {hideIconFromAssistiveTechnology} from '../icon/icon-accessibility.js';

@customElement('ents-wishlist-item-slider-item')
export class WishlistItemSlider extends LitElement {

  @consume({context: i18nContext})
  @property({attribute: false})
  private messages!: Messages;

  @property({
    type: Object,
    attribute: 'product',
  }) protected product!: Product;

  @property({type: String})
  private collectionId!: string;

  @property({type: Boolean, attribute: 'is-in-collection'})
  private isInCollection: boolean = false;

  connectedCallback() {
    super.connectedCallback();
  }

  // use this syntax to make it work in Safari 14sharedIcon
  // working: static get styles() { return css`...` }
  // not working: static styles = css`...`
  static get styles(): CSSResult {
    return unsafeCSS(css);
  }

  private handleClick(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    const event = this.isInCollection ? 'remove-product-from-collection' : 'add-product-to-collection';
    this.dispatchEvent(new CustomEvent(event, {
      detail: {
        collectionId: this.collectionId,
        product: this.product,
      }
    }));
  }

  private getAltDescriptionOfProduct() {
    return `${this.product?.brandName} ${this.product?.name} ${this.product.colorName ?? ''}`;
  }

  private getCallToActionMessageForList() {
    return this.isInCollection ?
      this.messages['ents_wishlist_product_cta_removeFromList'] :
      this.messages['ents_wishlist_product_cta_addToList'];
  }

  private getCTAConfig() {
    return this.isInCollection
      ? {
        icon: checkmarkIcon(),
        class: 'ents-wishlist-item-slider__check-icon',
      }
      : {
        icon: plusIcon(),
        class: 'ents-wishlist-item-slider__plus-icon',
      };
  }

  private renderCTAIcon(): TemplateResult {
    const ctaConfig = this.getCTAConfig();
    return html`
      <span class="${ctaConfig.class}">
        ${hideIconFromAssistiveTechnology(ctaConfig.icon)}
      </span>
    `;
  }

  protected render(): TemplateResult {
    return html`
      <label aria-label="${this.getAltDescriptionOfProduct()}. ${this.getCallToActionMessageForList()}"
             class="ents-wishlist-item-slider__button ${classMap({
               'ents-wishlist-item-slider__button--in-collection': this.isInCollection
             })}" data-test-ents-wishlist-slider-item-button="${this.product.syan}">
        <input type="checkbox" @click="${this.handleClick}" .checked="${this.isInCollection}"
               class="sr-only"/>
        <ents-wishlist-image
          data-test-ents-wishlist-slider-item-picture
          image-url="${this.product.defaultImageUrls[0].url}"
          sizes="auto"
          alt-text="${this.product?.brandName} ${this.product?.name} ${this.product.colorName ?? ''}"
          ?is-beauty="${this.product?.isBeauty === true}"
          brand-code="${this.product?.brandCode}"
        ></ents-wishlist-image>
        ${this.renderCTAIcon()}
      </label>
    `;
  }
}
